import React from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
} from 'antd';
import { Adaptation } from '../../types';
import { ADAPTATION_CATEGORIES } from '../../constants';
import { formatParametersText, formatAssociationsText } from '../utils/adaptationUtils';

const { Option } = Select;
const { TextArea } = Input;

interface AdaptationFormProps {
  form: any;
  adaptation: Adaptation | null;
  onSave: (values: any) => Promise<void>;
  onCancel: () => void;
}

const AdaptationForm: React.FC<AdaptationFormProps> = ({
  form,
  adaptation,
  onSave,
  onCancel,
}) => {
  const handleSubmit = async (values: any) => {
    try {
      // Process the form values
      const processedValues = {
        ...values,
        scientificParameters: values.scientificParameters.split('\n').filter((p: string) => p.trim()),
        exerciseAssociations: values.exerciseAssociations.split(',').map((a: string) => a.trim()).filter((a: string) => a),
      };
      await onSave(processedValues);
    } catch (error) {
      console.error('Error saving adaptation:', error);
    }
  };

  // Prepare initial values for the form
  const initialValues = adaptation ? {
    ...adaptation,
    scientificParameters: formatParametersText(adaptation.scientificParameters),
    exerciseAssociations: formatAssociationsText(adaptation.exerciseAssociations),
  } : {};

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            name="name"
            label="Adaptation Name"
            rules={[{ required: true, message: 'Please enter adaptation name' }]}
          >
            <Input placeholder="Enter adaptation name" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {Object.values(ADAPTATION_CATEGORIES).map(category => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      
      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: 'Please enter description' }]}
      >
        <TextArea 
          rows={3} 
          placeholder="Enter detailed description of the adaptation mechanism"
        />
      </Form.Item>

      <Form.Item
        name="scientificParameters"
        label="Scientific Parameters (one per line)"
        rules={[{ required: true, message: 'Please enter scientific parameters' }]}
      >
        <TextArea 
          rows={6} 
          placeholder="Enter scientific parameters, one per line"
        />
      </Form.Item>

      <Form.Item
        name="exerciseAssociations"
        label="Exercise Associations (comma separated)"
      >
        <TextArea 
          rows={2} 
          placeholder="Enter exercise types or categories, separated by commas"
        />
      </Form.Item>

      <Row justify="end" style={{ marginTop: 24 }}>
        <Col>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {adaptation ? 'Update' : 'Create'}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default AdaptationForm; 